import axios from '../utils/axios';

export interface TrialUser {
  id: string;
  name: string;
  email: string;
  status: 'Active' | 'Inactive';
  trial_expires_at: string | null;
  created_at: string | null;
  days_remaining: number | null;
  is_trial_expired: boolean;
  last_activity: string | null;
  activity_count: number;
  has_organization_access: boolean;
  organization_names: string[];
  recent_activities: ActivityRecord[];
}

export interface ActivityRecord {
  activity_type: string;
  activity_data: any;
  created_at: string;
}

export interface TrialUsageStats {
  total_trial_users: number;
  active_trial_users: number;
  expired_trial_users: number;
  users_with_activity: number;
  average_activity_per_user: number;
  trial_conversion_rate: number;
}

export interface ExtendTrialRequest {
  user_id: string;
  extension_days: number;
  reason?: string;
}

export interface ExtendTrialResponse {
  success: boolean;
  message: string;
  new_trial_expires_at: string | null;
}

export interface TrialUserFilter {
  name?: string;
  email?: string;
  status?: 'Active' | 'Inactive';
  trial_status?: 'active' | 'expired' | 'no_trial';
  has_organization?: boolean;
  activity_count_min?: number;
  activity_count_max?: number;
  days_remaining_min?: number;
  days_remaining_max?: number;
  created_after?: string;
  created_before?: string;
}

export interface TrialUserSort {
  field: 'name' | 'email' | 'status' | 'trial_expires_at' | 'created_at' | 'activity_count' | 'days_remaining';
  direction: 'asc' | 'desc';
}

export interface TrialUserQueryParams {
  limit?: number;
  offset?: number;
  filters?: TrialUserFilter;
  sort?: TrialUserSort;
}

class TrialUserService {
  
  async getTrialUsers(params: {
    include_expired?: boolean;
    include_active?: boolean;
    limit?: number;
    offset?: number;
  } = {}, token: string): Promise<TrialUser[]> {
    const searchParams = new URLSearchParams();
    
    if (params.include_expired !== undefined) {
      searchParams.append('include_expired', params.include_expired.toString());
    }
    if (params.include_active !== undefined) {
      searchParams.append('include_active', params.include_active.toString());
    }
    if (params.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params.offset) {
      searchParams.append('offset', params.offset.toString());
    }

    const url = `/api/admin/trial-users${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async searchTrialUsers(queryParams: TrialUserQueryParams, token: string): Promise<TrialUser[]> {
    console.log('Sending request to search API with params:', queryParams);
    
    // Ensure clean payload structure
    const cleanParams = {
      limit: queryParams.limit || 25,
      offset: queryParams.offset || 0,
      ...(queryParams.filters && Object.keys(queryParams.filters).length > 0 ? { filters: queryParams.filters } : {}),
      ...(queryParams.sort ? { sort: queryParams.sort } : {})
    };
    
    console.log('Cleaned params:', JSON.stringify(cleanParams, null, 2));
    
    const response = await axios.post('/api/admin/trial-users/search', cleanParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  async getTrialUsageStats(token: string): Promise<TrialUsageStats> {
    const response = await axios.get('/api/admin/trial-stats', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async extendUserTrial(request: ExtendTrialRequest, token: string): Promise<ExtendTrialResponse> {
    const response = await axios.post('/api/admin/extend-trial', request, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async deactivateUser(userId: string, token: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(`/api/admin/deactivate-user/${userId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async reactivateUser(userId: string, token: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(`/api/admin/reactivate-user/${userId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async getUserDetailedActivity(userEmail: string, limit = 50, token: string): Promise<{ activities: ActivityRecord[] }> {
    const response = await axios.get(`/api/admin/user-activity/${encodeURIComponent(userEmail)}?limit=${limit}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  formatActivityType(activityType: string): string {
    // Convert snake_case to readable format
    return activityType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatDateRelative(dateString: string | null): string {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else {
      return 'Just now';
    }
  }

  getTrialStatusColor(user: TrialUser): string {
    if (!user.trial_expires_at) return 'gray';
    if (user.is_trial_expired) return 'red';
    if (user.days_remaining !== null && user.days_remaining <= 3) return 'orange';
    return 'green';
  }

  getTrialStatusText(user: TrialUser): string {
    if (!user.trial_expires_at) return 'No trial';
    if (user.is_trial_expired) return 'Expired';
    if (user.days_remaining !== null) {
      return `${user.days_remaining} day${user.days_remaining === 1 ? '' : 's'} left`;
    }
    return 'Active';
  }
}

export const trialUserService = new TrialUserService();
